import React, { useRef, useState } from "react";
import { Camera, Image as ImageIcon, Link2, X, Loader2, FolderOpen, RotateCcw, CheckCircle2, LayoutGrid } from "lucide-react";
import { ImageLibraryDialog } from "@/components/ImageLibrary";
import { toast } from "sonner";
import { IMAGE_ACCEPT, isImageFile, uploadImageFile, uploadErrorText } from "@/lib/imageUpload";

const btn = "inline-flex items-center gap-1 rounded-full border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 px-2.5 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-60";

// Shared Gallery / Camera / Upload File / URL controls with progress + retry. onFiles receives File[].
export function UploadControls({ onFiles, onUrl, multiple = false, busy, progress, failed, onRetry, disabled, testid, libraryCategory, librarySuggest }) {
  const [libOpen, setLibOpen] = useState(false);
  const camRef = useRef(null);
  const galRef = useRef(null);
  const fileRef = useRef(null);
  const [urlMode, setUrlMode] = useState(false);
  const [urlDraft, setUrlDraft] = useState("");
  const pick = (e) => { const files = Array.from(e.target.files || []); e.target.value = ""; if (files.length) onFiles(files); };
  const commitUrl = () => {
    const u = urlDraft.trim();
    try { const p = new URL(u); if (!/^https?:$/.test(p.protocol)) throw new Error(); } catch { toast.error("Please paste a valid https:// image link"); return; }
    onUrl(u); toast.success("Image link added"); setUrlMode(false); setUrlDraft("");
  };
  const off = disabled || busy;
  return (
    <>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-camera-input`} />
      <input ref={galRef} type="file" accept="image/*" multiple={multiple} className="hidden" onChange={pick} data-testid={`${testid}-gallery-input`} />
      <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} multiple={multiple} className="hidden" onChange={pick} data-testid={`${testid}-file-input`} />
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        <button type="button" onClick={() => galRef.current?.click()} disabled={off} className={btn} data-testid={`${testid}-gallery-btn`}><ImageIcon className="w-3.5 h-3.5" /> Gallery</button>
        <button type="button" onClick={() => camRef.current?.click()} disabled={off} className={btn} data-testid={`${testid}-camera-btn`}><Camera className="w-3.5 h-3.5" /> Camera</button>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={off} className={btn} data-testid={`${testid}-file-btn`}><FolderOpen className="w-3.5 h-3.5" /> Upload File</button>
        {libraryCategory && <button type="button" onClick={() => setLibOpen(true)} disabled={off} className={`${btn} border-amber-200 text-amber-800 hover:bg-amber-50`} data-testid={`${testid}-library-btn`}><LayoutGrid className="w-3.5 h-3.5" /> VerB Library</button>}
        <button type="button" onClick={() => setUrlMode((m) => !m)} disabled={off} className={`${btn} ${urlMode ? "border-emerald-500 text-emerald-700 bg-emerald-50" : ""}`} data-testid={`${testid}-url-btn`}><Link2 className="w-3.5 h-3.5" /> URL</button>
      </div>
      {urlMode && (
        <div className="mt-2 flex items-center gap-2" data-testid={`${testid}-url-row`}>
          <input value={urlDraft} onChange={(e) => setUrlDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitUrl(); } }} type="url" inputMode="url" placeholder="https://..." className="flex-1 min-w-0 border border-slate-200 rounded-xl px-3 py-2 text-base sm:text-sm outline-none focus:border-emerald-500" data-testid={`${testid}-url-input`} />
          <button type="button" onClick={commitUrl} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 text-xs font-semibold" data-testid={`${testid}-url-save`}>Save</button>
          <button type="button" onClick={() => { setUrlMode(false); setUrlDraft(""); }} aria-label="Cancel URL" className="p-2 rounded-full hover:bg-slate-100"><X className="w-3.5 h-3.5 text-slate-500" /></button>
        </div>
      )}
      <div className="text-[11px] text-slate-400 mt-1">JPG, PNG or WEBP · large camera photos are resized automatically</div>
      {libraryCategory && <ImageLibraryDialog open={libOpen} onOpenChange={setLibOpen} initialCategory={libraryCategory} suggest={librarySuggest} onSelect={(u) => { onUrl(u); toast.success("Library image selected — remember to save"); }} />}
      {busy && (
        <div className="mt-1.5 max-w-[14rem]" data-testid={`${testid}-progress`}>
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading… {progress ? `${progress}%` : ""}</div>
          <div className="h-1 rounded-full bg-slate-100 overflow-hidden mt-0.5"><div className="h-full bg-emerald-600 transition-[width]" style={{ width: `${progress || 5}%` }} /></div>
        </div>
      )}
      {failed && !busy && (
        <div className="mt-1.5 text-[11px] text-rose-600 inline-flex items-center gap-2" data-testid={`${testid}-error`}>
          {failed}
          <button type="button" onClick={onRetry} className="inline-flex items-center gap-1 font-semibold text-emerald-700" data-testid={`${testid}-retry`}><RotateCcw className="w-3 h-3" /> Retry</button>
        </div>
      )}
    </>
  );
}

// Hook: upload a list of image files sequentially, tracking progress, failures and retry.
export function useImageUploads(onUploaded) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState("");
  const lastRef = useRef([]);
  const run = async (files) => {
    const valid = files.filter((f) => { if (isImageFile(f)) return true; toast.error(`${f.name || "File"}: please choose a JPG, PNG or WEBP image`); return false; });
    if (!valid.length || busy) return;
    lastRef.current = valid;
    setBusy(true); setFailed("");
    const urls = [];
    const left = [];
    for (const f of valid) {
      setProgress(0);
      try { urls.push((await uploadImageFile(f, setProgress)).url); }
      catch (e) { left.push(f); setFailed(`${f.name || "Image"}: ${uploadErrorText(e)}`); }
    }
    lastRef.current = left;
    setBusy(false);
    if (urls.length) { onUploaded(urls); toast.success(urls.length > 1 ? `${urls.length} images uploaded` : "Image uploaded"); }
  };
  return { busy, progress, failed, run, retry: () => run(lastRef.current) };
}

export const UploadedTick = () => <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />;
